import React, { Key, useRef, useState } from 'react';
import {
  Button,
  Form,
  FormTitle,
  Modal,
  Radio,
  SubContent,
  Upload,
  Wrapper,
  message,
  useBreadcrumbRoutes,
} from '@maxtropy/components';
import { Space, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { apiV2EnvMonitorConfigGetPost, apiV2EnvMonitorConfigSavePost } from '@maxtropy/device-customer-apis-v2';
import DeviceSection from './DeviceSection';
import DeviceSelectModal from './DeviceSelectModal';
import ThresholdConfigModal from './ThresholdConfigModal';
import { DeviceConfig, DeviceThresholds, DeviceSelectModalRef, DeviceCategory, DEVICE_CATEGORY } from './types';
import styles from './index.module.scss';

const DEVICE_SECTIONS = [
  { type: DEVICE_CATEGORY.Gas, title: '废气监测设备' },
  { type: DEVICE_CATEGORY.Water, title: '废水监测设备' },
] as const;

type DeviceResponseBase = {
  id?: number;
  deviceId?: number;
  deviceName?: string;
  sortOrder?: number;
};

const EnvMonitorKanbanConfig: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm<{ fileKey?: string }>();
  const deviceSelectRef = useRef<DeviceSelectModalRef | null>(null);

  // 编辑模式
  const [isEdit, setIsEdit] = useState(false);

  // 设备数据（编辑时直接修改，取消时从缓存恢复）
  const [gasDevices, setGasDevices] = useState<DeviceConfig[]>([]);
  const [waterDevices, setWaterDevices] = useState<DeviceConfig[]>([]);
  const [showThreshold, setShowThreshold] = useState<boolean>(true);

  // 缓存数据（取消编辑时恢复）
  const cacheRef = useRef<{
    gasDevices: DeviceConfig[];
    waterDevices: DeviceConfig[];
    showThreshold: boolean;
  }>({ gasDevices: [], waterDevices: [], showThreshold: true });

  // 弹窗控制
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<DeviceCategory>(DEVICE_CATEGORY.Gas);
  const [editingDeviceId, setEditingDeviceId] = useState<number | null>(null);

  const getDevicesByType = (type: DeviceCategory) => (type === DEVICE_CATEGORY.Gas ? gasDevices : waterDevices);

  const setDevicesByType = (type: DeviceCategory, updater: (prev: DeviceConfig[]) => DeviceConfig[]) => {
    if (type === DEVICE_CATEGORY.Gas) {
      setGasDevices(updater);
      return;
    }
    setWaterDevices(updater);
  };

  const getThresholdPayload = (devices: DeviceConfig[], type: DeviceCategory) =>
    devices.map((device, index) => ({
      deviceId: device.deviceId,
      ...(type === DEVICE_CATEGORY.Gas
        ? {
            noxLimit: device.thresholds?.noxLimit,
            nmhcLimit: device.thresholds?.nmhcLimit,
          }
        : {
            ammoniaNitrogenLimit: device.thresholds?.ammoniaNitrogenLimit,
            totalPhosphorusLimit: device.thresholds?.totalPhosphorusLimit,
            codLimit: device.thresholds?.codLimit,
            totalNitrogenLimit: device.thresholds?.totalNitrogenLimit,
            phLowerLimit: device.thresholds?.phLowerLimit,
            phUpperLimit: device.thresholds?.phUpperLimit,
          }),
      sortOrder: index + 1,
    }));

  const mapDevices = <T extends DeviceResponseBase>(
    devices: T[] | null | undefined,
    thresholdsMapper: (device: T) => DeviceThresholds
  ): DeviceConfig[] =>
    (devices ?? []).map(device => ({
      id: device.id ?? 0,
      deviceId: device.deviceId ?? 0,
      name: device.deviceName ?? '',
      code: '',
      thresholds: thresholdsMapper(device),
      sortOrder: device.sortOrder,
    }));

  const closeDeviceModal = () => {
    setDeviceModalOpen(false);
  };

  const closeThresholdModal = () => {
    setThresholdModalOpen(false);
    setEditingDeviceId(null);
  };

  const activeDevices = getDevicesByType(editingType);
  const oppositeDevices = getDevicesByType(
    editingType === DEVICE_CATEGORY.Gas ? DEVICE_CATEGORY.Water : DEVICE_CATEGORY.Gas
  );
  const editingDevice =
    editingDeviceId == null ? null : activeDevices.find(device => device.deviceId === editingDeviceId) ?? null;
  const bindDeviceIds: Key[] = activeDevices.map(device => device.deviceId);

  // 获取配置
  const { loading: pageLoading, run: loadConfig } = useRequest(
    async () => {
      const res = await apiV2EnvMonitorConfigGetPost({});
      const exhaustList = mapDevices(res.exhaustDevices, device => ({
        noxLimit: device.noxLimit,
        nmhcLimit: device.nmhcLimit,
      }));
      const wasteWaterList = mapDevices(res.wastewaterDevices, device => ({
        ammoniaNitrogenLimit: device.ammoniaNitrogenLimit,
        totalPhosphorusLimit: device.totalPhosphorusLimit,
        codLimit: device.codLimit,
        totalNitrogenLimit: device.totalNitrogenLimit,
        phLowerLimit: device.phLowerLimit,
        phUpperLimit: device.phUpperLimit,
      }));
      const showThresholdLine = res.showThresholdLine === 1;

      setGasDevices(exhaustList);
      setWaterDevices(wasteWaterList);
      setShowThreshold(showThresholdLine);
      // 同步缓存
      cacheRef.current = { gasDevices: exhaustList, waterDevices: wasteWaterList, showThreshold: showThresholdLine };
    },
    { manual: false }
  );

  // 保存配置
  const { loading: saveLoading, run: handleSave } = useRequest(
    async () => {
      await apiV2EnvMonitorConfigSavePost({
        showThresholdLine: showThreshold ? 1 : 0,
        exhaustDevices: getThresholdPayload(gasDevices, DEVICE_CATEGORY.Gas),
        wastewaterDevices: getThresholdPayload(waterDevices, DEVICE_CATEGORY.Water),
        fileKey: form.getFieldValue('fileKey'),
      });
      message.success('保存成功');
      setIsEdit(false);
      await loadConfig();
    },
    { manual: true }
  );

  // 进入编辑模式
  const handleEdit = () => {
    cacheRef.current = {
      gasDevices: [...gasDevices],
      waterDevices: [...waterDevices],
      showThreshold,
    };
    setIsEdit(true);
  };

  // 取消编辑
  const handleCancel = () => {
    setGasDevices(cacheRef.current.gasDevices);
    setWaterDevices(cacheRef.current.waterDevices);
    setShowThreshold(cacheRef.current.showThreshold);
    setIsEdit(false);
  };

  // 打开设备选择弹窗
  const openDeviceModal = (type: DeviceCategory) => {
    setEditingType(type);
    setDeviceModalOpen(true);
  };

  // 设备选择弹窗确认
  const handleDeviceModalOk = () => {
    const selectedRows = deviceSelectRef.current?.selectedRows ?? [];

    if (selectedRows.length === 0) {
      closeDeviceModal();
      return;
    }

    const currentDeviceIds = new Set(activeDevices.map(device => device.deviceId));
    const otherDeviceIds = new Set(oppositeDevices.map(device => device.deviceId));

    const conflictDevices = selectedRows.filter(d => otherDeviceIds.has(d.deviceId));
    if (conflictDevices.length > 0) {
      message.warning(
        `设备【${conflictDevices.map(i => i.name).join('、')}】已被${
          editingType === DEVICE_CATEGORY.Gas ? '废水' : '废气'
        }监测选中`
      );
      return;
    }

    // 过滤掉当前类型已存在的设备
    const newDevices = selectedRows
      .filter(d => !currentDeviceIds.has(d.deviceId))
      .map(d => ({ id: 0, deviceId: d.deviceId, name: d.name, code: d.code }));

    if (newDevices.length === 0 && conflictDevices.length === 0) {
      message.info('选择的设备已存在');
      closeDeviceModal();
      return;
    }

    setDevicesByType(editingType, prev => [...prev, ...newDevices]);
    closeDeviceModal();
  };

  // 删除设备
  const handleDeleteDevice = (type: DeviceCategory, deviceId: number) => {
    setDevicesByType(type, prev => prev.filter(device => device.deviceId !== deviceId));
  };

  // 打开阈值配置弹窗
  const openThresholdModal = (device: DeviceConfig) => {
    if (!device.deviceType) return;
    setEditingType(device.deviceType);
    setEditingDeviceId(device.deviceId);
    setThresholdModalOpen(true);
  };

  // 阈值配置确认
  const handleThresholdOk = (thresholds: DeviceThresholds) => {
    if (editingDeviceId == null) return;

    setDevicesByType(editingType, prev =>
      prev.map(device => (device.deviceId === editingDeviceId ? { ...device, thresholds } : device))
    );
    closeThresholdModal();
  };

  const routes = [...(breadcrumbRoutes?.routes ?? []), { name: '配置' }];

  return (
    <Wrapper routes={routes}>
      <Spin spinning={pageLoading || saveLoading}>
        <FormTitle title="环保排放实时监测看板配置" />

        {DEVICE_SECTIONS.map(section => (
          <DeviceSection
            key={section.type}
            title={section.title}
            devices={getDevicesByType(section.type)}
            isEdit={isEdit}
            onAddDevice={() => openDeviceModal(section.type)}
            onDeleteDevice={id => handleDeleteDevice(section.type, id)}
            onConfigThreshold={openThresholdModal}
            deviceType={section.type}
          />
        ))}

        <SubContent title="展示设置">
          <Form.Item label="是否展示指标上/下限">
            <Radio.Group value={showThreshold} disabled={!isEdit} onChange={e => setShowThreshold(e.target.value)}>
              <Radio value={true}>展示</Radio>
              <Radio value={false}>不展示</Radio>
            </Radio.Group>
          </Form.Item>
        </SubContent>

        <SubContent title="排放计划配置表">
          <Form form={form}>
            <div className={styles.emissionPlan}>
              <Button type="link" disabled={!isEdit} href="/api/v2/env/monitor/emission-plan/export">
                下载
              </Button>
              <Form.Item name="fileKey" noStyle>
                <Upload listType="text" accept={['.xlsx']} uploadText="上传" fileSize={50} disabled={!isEdit}>
                  <Button type="link" disabled={!isEdit}>
                    上传
                  </Button>
                </Upload>
              </Form.Item>
            </div>
          </Form>
        </SubContent>

        <Space className={styles.stickyFooter} size={8}>
          {isEdit ? (
            <>
              <Button type="primary" loading={saveLoading} onClick={handleSave}>
                保存
              </Button>
              <Button onClick={handleCancel}>取消</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={handleEdit}>
                编辑
              </Button>
              <Button onClick={() => navigate('/env/monitor/kanban')}>返回</Button>
            </>
          )}
        </Space>

        {deviceModalOpen && (
          <Modal
            title="设备选择"
            open={deviceModalOpen}
            size="big"
            destroyOnClose
            onOk={handleDeviceModalOk}
            onCancel={closeDeviceModal}
          >
            <DeviceSelectModal
              ref={deviceSelectRef}
              bindDeviceIds={bindDeviceIds}
              existingDeviceIds={bindDeviceIds as number[]}
            />
          </Modal>
        )}

        {thresholdModalOpen && editingDevice && (
          <ThresholdConfigModal
            open={thresholdModalOpen}
            isEdit={editingDevice.thresholds !== undefined}
            deviceType={editingType}
            deviceName={editingDevice.name}
            currentThresholds={editingDevice.thresholds}
            onOk={handleThresholdOk}
            onCancel={closeThresholdModal}
          />
        )}
      </Spin>
    </Wrapper>
  );
};

export default EnvMonitorKanbanConfig;
